import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Trash2, Play, RefreshCw, Square, Loader2, Database, Download, Save } from 'lucide-react';
import request from '../../api/request';

interface LogEntry {
    timestamp: string;
    level: string;
    message: string;
}

interface AnalysisLogModalProps {
    onClose: () => void;
}

const AnalysisLogModal: React.FC<AnalysisLogModalProps> = ({ onClose }) => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoopRunning, setIsLoopRunning] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const ALL_STATUSES = [
        { value: 'Unpaid', label: '未付款' },
        { value: 'Shipped', label: '已发货' },
        { value: 'Signed', label: '已签收' },
        { value: 'Returned', label: '已退回' },
        { value: 'Cancelled', label: '已取消' },
        { value: 'Rported', label: '已报告' },
    ];

    const [pullDateFrom, setPullDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    });
    const [pullDateTo, setPullDateTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [includeStatuses, setIncludeStatuses] = useState<string[]>([]);
    const [autoIncludeStatuses, setAutoIncludeStatuses] = useState<string[]>([]);
    const [isPulling, setIsPulling] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const logContainerRef = useRef<HTMLDivElement>(null);
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const fetchLogs = async () => {
        try {
            const response = await request.get('/api/settings/order-analysis/logs');
            setLogs(response.data.logs || []);
            // 滚动到底部
            setTimeout(() => {
                if (logContainerRef.current) {
                    logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
                }
            }, 100);
        } catch (e) {
            console.error('获取日志失败:', e);
        }
    };

    const clearLogs = async () => {
        try {
            await request.delete('/api/settings/order-analysis/logs');
            setLogs([]);
        } catch (e) {
            console.error('清空日志失败:', e);
        }
    };

    const loadConfig = async () => {
        try {
            const response = await request.get('/api/settings');
            const d = response.data;
            if (d.orderPullDateFrom) setPullDateFrom(d.orderPullDateFrom);
            if (d.orderPullDateTo) setPullDateTo(d.orderPullDateTo);
            if (d.orderPullIncludeStatuses) {
                try { setIncludeStatuses(JSON.parse(d.orderPullIncludeStatuses)); }
                catch { setIncludeStatuses([]); }
            }
            if (d.orderPullAutoIncludeStatuses) {
                try { setAutoIncludeStatuses(JSON.parse(d.orderPullAutoIncludeStatuses)); }
                catch { setAutoIncludeStatuses([]); }
            }
        } catch (e) {
            console.error('加载拉取配置失败:', e);
        }
    };

    const saveConfig = async () => {
        setIsSaving(true);
        try {
            await request.put('/api/settings', {
                orderPullDateFrom: pullDateFrom,
                orderPullDateTo: pullDateTo,
                orderPullIncludeStatuses: JSON.stringify(includeStatuses),
                orderPullAutoIncludeStatuses: JSON.stringify(autoIncludeStatuses),
            });
            const manMsg = includeStatuses.length > 0 ? includeStatuses.join(', ') : '全部';
            const autoMsg = autoIncludeStatuses.length > 0 ? autoIncludeStatuses.join(', ') : '全部';
            alert(`✅ 配置已保存\n开始日期: ${pullDateFrom}\n结束日期: ${pullDateTo}\n拉取状态(手动): ${manMsg}\n拉取状态(自动): ${autoMsg}`);
        } catch (e: any) {
            alert('❌ 保存失败: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setIsSaving(false);
        }
    };

    const toggleIncludeStatus = (status: string) => {
        setIncludeStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const toggleAutoIncludeStatus = (status: string) => {
        setAutoIncludeStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const pullOrders = async () => {
        setIsPulling(true);
        try {
            const response = await request.post('/api/pos/orders/pull', {
                date_from: pullDateFrom,
                date_to: pullDateTo,
                include_statuses: includeStatuses.length > 0 ? includeStatuses : null,
            });
            const data = response.data;
            alert(`✅ ${data.message}\n新增 ${data.pulled}, 更新 ${data.updated}, 跳过 ${data.skipped}`);
            fetchLogs();
        } catch (e: any) {
            const msg = e?.response?.data?.detail || '拉取失败，请检查 POS 连接';
            alert('❌ ' + msg);
            console.error('拉取订单失败:', e);
        } finally {
            setIsPulling(false);
        }
    };

    const clearDatabase = async () => {
        if (!window.confirm('⚠️ 确定要清理所有订单追踪数据吗？\n\n此操作将清空 order_trackings 表并重置序列号，不可恢复！')) {
            return;
        }
        try {
            const response = await request.delete('/api/settings/order-analysis/trackings');
            if (response.data.success) {
                alert('✅ 订单追踪数据已全部清理，序列号已重置');
            }
        } catch (e) {
            console.error('清理数据库失败:', e);
            alert('❌ 清理失败，请查看控制台日志');
        }
    };

    const triggerAnalysis = async () => {
        setIsLoading(true);
        try {
            await request.post('/api/settings/order-analysis/trigger');
        } catch (e) {
            console.error('触发分析失败:', e);
        } finally {
            setIsLoading(false);
        }
    };

    const startLoop = async () => {
        try {
            await request.post('/api/settings/order-analysis/start-loop');
            setIsLoopRunning(true);
            setAutoRefresh(true);
        } catch (e) {
            console.error('启动循环失败:', e);
        }
    };

    const stopLoop = async () => {
        try {
            await request.post('/api/settings/order-analysis/stop-loop');
            setIsLoopRunning(false);
        } catch (e) {
            console.error('停止循环失败:', e);
        }
    };

    // 初始加载
    useEffect(() => {
        fetchLogs();
        loadConfig();
    }, []);

    // 自动刷新
    useEffect(() => {
        if (autoRefresh) {
            refreshIntervalRef.current = setInterval(fetchLogs, 3000);
        } else {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        }
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [autoRefresh]);

    const formatTime = (isoString: string) => {
        try {
            const date = new Date(isoString);
            return date.toLocaleTimeString('zh-CN', { hour12: false });
        } catch {
            return isoString;
        }
    };

    const getLevelColor = (level: string) => {
        switch (level.toUpperCase()) {
            case 'ERROR': return 'text-red-500';
            case 'WARNING': return 'text-yellow-500';
            case 'INFO': return 'text-blue-500';
            default: return 'text-muted-foreground';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        订单分析日志
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Control Buttons */}
                <div className="flex flex-wrap gap-2 p-4 border-b border-border">
                    <button
                        onClick={triggerAnalysis}
                        disabled={isLoading}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        单次触发
                    </button>

                    {!isLoopRunning ? (
                        <button
                            onClick={startLoop}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            <RefreshCw className="w-4 h-4" />
                            启动循环
                        </button>
                    ) : (
                        <button
                            onClick={stopLoop}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            <Square className="w-4 h-4" />
                            停止循环
                        </button>
                    )}

                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border ${autoRefresh ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-border hover:bg-muted'
                            }`}
                    >
                        <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                        自动刷新
                    </button>

                    <button
                        onClick={fetchLogs}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted"
                    >
                        <RefreshCw className="w-4 h-4" />
                        刷新
                    </button>

                    <button
                        onClick={clearLogs}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50"
                    >
                        <Trash2 className="w-4 h-4" />
                        清空日志
                    </button>

                    <button
                        onClick={clearDatabase}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-red-700 rounded-md hover:bg-red-800"
                    >
                        <Database className="w-4 h-4" />
                        清理数据库
                    </button>
                </div>

                {/* Pull Orders Row */}
                <div className="px-4 py-2 border-b border-border bg-muted/30 space-y-2">
                    <div className="flex flex-wrap gap-2 items-center">
                        <label className="text-xs text-muted-foreground">开始:</label>
                        <input type="date" value={pullDateFrom} onChange={(e) => setPullDateFrom(e.target.value)}
                            className="text-xs border border-border rounded px-2 py-1 bg-background" />
                        <label className="text-xs text-muted-foreground">结束:</label>
                        <input type="date" value={pullDateTo} onChange={(e) => setPullDateTo(e.target.value)}
                            className="text-xs border border-border rounded px-2 py-1 bg-background" />
                        <span className="text-xs text-muted-foreground mx-1">|</span>
                        <button onClick={saveConfig} disabled={isSaving}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50">
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            保存配置
                        </button>
                        <button onClick={pullOrders} disabled={isPulling}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {isPulling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            拉取订单
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">拉取状态(手动):</span>
                        {ALL_STATUSES.map(st => (
                            <label key={st.value} className="flex items-center gap-1 text-xs cursor-pointer">
                                <input type="checkbox" checked={includeStatuses.includes(st.value)}
                                    onChange={() => toggleIncludeStatus(st.value)}
                                    className="w-3 h-3" />
                                {st.label}
                            </label>
                        ))}
                        <span className="text-[10px] text-muted-foreground">勾选=拉取, 全不选=全部</span>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">拉取状态(自动):</span>
                        {ALL_STATUSES.map(st => (
                            <label key={st.value} className="flex items-center gap-1 text-xs cursor-pointer">
                                <input type="checkbox" checked={autoIncludeStatuses.includes(st.value)}
                                    onChange={() => toggleAutoIncludeStatus(st.value)}
                                    className="w-3 h-3" />
                                {st.label}
                            </label>
                        ))}
                        <span className="text-[10px] text-muted-foreground">勾选=拉取, 全不选=全部</span>
                    </div>
                </div>

                {/* Log Content */}
                <div
                    ref={logContainerRef}
                    className="flex-1 overflow-y-auto p-4 font-mono text-xs bg-black/5"
                    style={{ maxHeight: '400px' }}
                >
                    {logs.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8">
                            暂无日志，点击"单次触发"或"启动循环"开始
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {logs.map((log, index) => {
                                const isSeparator = log.message === '__SEP__';
                                const isError = log.level === 'ERROR';
                                const isSuccess = log.message.includes('任务完成') || log.message.includes('✅');

                                return (
                                    <div
                                        key={index}
                                        className={`flex gap-2 ${isSeparator ? 'py-3 font-bold text-primary border-t-2 border-primary/30 mt-3 pt-3' : ''} ${isError ? 'bg-red-500/10 p-1 rounded' : ''}`}
                                    >
                                        <span className="text-muted-foreground flex-shrink-0 text-[10px] w-14">
                                            {!isSeparator && formatTime(log.timestamp)}
                                        </span>
                                        {!isSeparator && (
                                            <span className={`flex-shrink-0 w-10 text-[10px] ${getLevelColor(log.level)}`}>
                                                [{log.level}]
                                            </span>
                                        )}
                                        {!isSeparator && (
                                            <span className={`break-all ${isSuccess ? 'text-green-600 font-medium' : ''}`}>
                                                {log.message}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <span>共 {logs.length} 条日志</span>
                    {autoRefresh && <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> 每3秒自动刷新</span>}
                </div>
            </div>
        </div>
    );
};

export default AnalysisLogModal;
